import React, { useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import I18n from 'i18n-js';
import cx from 'classnames';

import { useViewPort } from '../../context/view-port';
import useMediaQuery from '../../hooks/useMediaQuery';
import Blur from '../blur/blur';
import TrackingMap from '../new-map/tracking-map';
import DragSheet from '../drag-sheet/drag-sheet';
import StatusCard from '../new-status-card/status-card';
import StayContactableCard from '../stay-contactable-card/stay-contactable-card';
import DriverCard from '../new-driver-card/driver-card';
import DeliveryDetails from '../delivery-details/delivery-details';
import CashPaymentCard from '../cash-payment-card/cash-payment-card';
import PayOnDeliveryPaymentDetails from '../pay-on-delivery-payment-details';
import PayOnDeliveryPaymentFooter from '../pay-on-delivery-payment-footer';
import OrderId from '../order-id/order-id';
import {
  isCompletedStatus,
  isCancelledStatus,
  isAfterHours,
  isPendingStatus,
} from '../../common/utils/status';
import styles from './booking-tracker.less';
import {
  HIDE_ORDER_HOURS,
  IN_TRANSIT_STATUS,
} from '../../constants/new-order-status';
import { FeatureFlagProvider } from '../../context/feature-flag';

const SMALL_SCREEN_HEIGHT = 360;

const BookingTracker = ({ featureFlag, map, driver, trip, userType, guid }) => {
  const { dimension, setContainerDimension } = useViewPort();
  const [md] = useMediaQuery('(min-width: 48em)');
  const containerRef = useRef();
  const isSmallScreen = dimension.containerHeight < SMALL_SCREEN_HEIGHT;

  useEffect(
    () => {
      if (containerRef.current) {
        setContainerDimension({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        });
      }
    },
    [md],
  );

  const hideDriverCard = useMemo(
    () => {
      const tripStatus = _get(trip, 'status', '');
      const tripEndTime = _get(trip, ['eta', 'endTime']);

      return (
        isCancelledStatus(tripStatus) ||
        (isCompletedStatus(tripStatus) &&
          isAfterHours(tripEndTime, HIDE_ORDER_HOURS))
      );
    },
    [trip.status, trip.eta.endTime],
  );

  const showBlur = useMemo(
    () => {
      const tripStatus = _get(trip, 'status', '');
      return md && isPendingStatus(tripStatus);
    },
    [md, trip.status],
  );

  const showStayContactable =
    !featureFlag.hideStayContactable && IN_TRANSIT_STATUS.includes(trip.status);
  const showDriverCard = driver && !hideDriverCard;
  const showDriverCardInExpandedView = showStayContactable && showDriverCard;

  return (
    <FeatureFlagProvider flag={featureFlag}>
      <div className={styles.container} ref={containerRef}>
        <div className={styles.mapContainer}>
          {showBlur && (
            <Blur>
              <div className={styles.mapBlockBanner}>
                <span className={styles.bannerContent}>
                  {I18n.t('dragSheet.expandedHeader.blockMapPrompt')}
                </span>
              </div>
            </Blur>
          )}
          <TrackingMap
            allPoints={map.allPoints}
            locations={map.locations}
            steps={map.steps}
            driver={map.driver}
            guid={guid}
            status={trip.status}
            userType={userType}
          />
        </div>

        <DragSheet
          expectationId={trip.expectationId}
          status={trip.status}
          payment={trip.payment}
          userType={userType}
          visibleContent={isExpanded => (
            <div
              className={cx(
                styles.visibleContent,
                !isExpanded && styles.nonExpandedVisibleContent,
              )}
            >
              <StatusCard
                eta={trip.eta.time}
                endTime={trip.eta.endTime}
                schedule={trip.eta.schedule}
                description={trip.eta.description}
                status={trip.status}
                itemTypeId={trip.itemType}
                expectationId={trip.expectationId}
                userType={userType}
                payment={trip.payment}
                isExpanded={isExpanded}
                hideCashlessPOD={featureFlag.hideCashlessPOD}
              />
              {/* check small screen only for collapse view */}
              {(isExpanded || !isSmallScreen) &&
                (showStayContactable ? (
                  <StayContactableCard
                    status={trip.status}
                    userType={userType}
                  />
                ) : (
                  showDriverCard && (
                    <DriverCard
                      bookingCode={trip.orderBookingCode}
                      driverInfo={driver.driverInfo}
                      vehicleInfo={driver.vehicleInfo}
                      contact={driver.contact}
                      status={trip.status}
                      userType={userType}
                    />
                  )
                ))}
            </div>
          )}
          otherContent={
            <div className={styles.otherContentContainer}>
              <div className={styles.otherContent}>
                {showDriverCardInExpandedView && (
                  <DriverCard
                    bookingCode={trip.orderBookingCode}
                    driverInfo={driver.driverInfo}
                    vehicleInfo={driver.vehicleInfo}
                    contact={driver.contact}
                    status={trip.status}
                    userType={userType}
                  />
                )}
                <DeliveryDetails
                  origin={trip.origin}
                  destinations={trip.destinations}
                  status={trip.status}
                  userType={userType}
                  guid={guid}
                />
                {!featureFlag.hideCashlessPOD ? (
                  <PayOnDeliveryPaymentDetails
                    status={trip.status}
                    userType={userType}
                    symbol={trip.payment.symbol}
                    payOnDelivery={trip.payment.payOnDelivery}
                  />
                ) : (
                  <CashPaymentCard
                    deliveryFee={trip.payment.deliveryFee}
                    symbol={trip.payment.symbol}
                    codAmount={trip.payment.cod}
                    total={trip.payment.total}
                    status={trip.status}
                    userType={userType}
                  />
                )}
                <OrderId id={trip.orderId} />
              </div>
              {!featureFlag.hideCashlessPOD && (
                <PayOnDeliveryPaymentFooter
                  status={trip.status}
                  codPaymentStatus={trip.codPaymentStatus}
                  symbol={trip.payment.symbol}
                  userType={userType}
                  payOnDelivery={trip.payment.payOnDelivery}
                />
              )}
            </div>
          }
        />
      </div>
    </FeatureFlagProvider>
  );
};

const CoordinatePropType = PropTypes.arrayOf(PropTypes.number);

// TODO:: Allow Booker to add instruction --> Add name to origin, to display AddInstructionBox
BookingTracker.propTypes = {
  featureFlag: PropTypes.shape({
    hideAddInstruction: PropTypes.bool,
    hideCallAndChat: PropTypes.bool,
    hidePhoto: PropTypes.bool,
    hideCashlessPOD: PropTypes.bool,
  }),
  map: PropTypes.shape({
    allPoints: PropTypes.arrayOf(CoordinatePropType),
    locations: PropTypes.shape({
      origin: PropTypes.shape({
        name: PropTypes.string,
        address: PropTypes.string,
        instruction: PropTypes.string,
        coordinates: CoordinatePropType,
      }),
      destinations: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          address: PropTypes.string,
          instruction: PropTypes.string,
          coordinates: CoordinatePropType,
        }),
      ),
    }),
    driver: PropTypes.shape({
      bearing: PropTypes.number,
      coordinates: CoordinatePropType,
    }),
  }),
  driver: PropTypes.shape({
    driverInfo: PropTypes.shape({
      fullname: PropTypes.string,
      profileSrc: PropTypes.string,
      rating: PropTypes.number,
    }),
    vehicleInfo: PropTypes.shape({
      model: PropTypes.string,
      make: PropTypes.string,
      license: PropTypes.string,
    }),
    contact: PropTypes.shape({
      phone: PropTypes.string,
      voipId: PropTypes.string,
      chatRoomId: PropTypes.string,
    }),
  }),
  trip: PropTypes.shape({
    expectationId: PropTypes.number,
    eta: PropTypes.shape({
      time: PropTypes.number,
      endTime: PropTypes.number,
      description: PropTypes.string,
      schedule: PropTypes.shape({
        from: PropTypes.number,
        to: PropTypes.number,
      }),
    }),
    itemType: PropTypes.number,
    status: PropTypes.string,
    codPaymentStatus: PropTypes.string,
    origin: PropTypes.shape({
      name: PropTypes.string,
      address: PropTypes.string,
      instruction: PropTypes.string,
      proof: PropTypes.shape({
        pop: PropTypes.shape({ url: PropTypes.arrayOf(PropTypes.string) }),
        pod: PropTypes.shape({ url: PropTypes.arrayOf(PropTypes.string) }),
        isExpired: PropTypes.bool,
      }),
    }),
    destinations: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        address: PropTypes.string,
        instruction: PropTypes.string,
        proof: PropTypes.shape({
          pop: PropTypes.shape({ url: PropTypes.arrayOf(PropTypes.string) }),
          pod: PropTypes.shape({ url: PropTypes.arrayOf(PropTypes.string) }),
          isExpired: PropTypes.bool,
        }),
      }),
    ),
    payment: PropTypes.shape({
      deliveryFee: PropTypes.string,
      cod: PropTypes.string,
      total: PropTypes.string,
      symbol: PropTypes.string,
      payOnDelivery: PropTypes.shape({
        items: PropTypes.arrayOf(
          PropTypes.shape({
            type: PropTypes.string,
            amount: PropTypes.string,
            paymentMethod: PropTypes.string,
          }),
        ),
        totalAmount: PropTypes.string,
        totalCashAmount: PropTypes.string,
        totalCashlessAmount: PropTypes.string,
      }),
    }),
    orderId: PropTypes.string,
  }),
  userType: PropTypes.shape({
    isReceiver: PropTypes.bool,
    isSender: PropTypes.bool,
  }),
  guid: PropTypes.string,
};

BookingTracker.defaultProps = {
  featureFlag: {
    hideAddInstruction: false,
    hideCallAndChat: false,
    hidePhoto: false,
    hideCashlessPOD: false,
    hideStayContactable: false,
  },
  map: {
    allPoints: null,
    locations: {
      origin: null,
      destinations: [],
    },
    steps: [],
    driver: {
      bearing: 0,
      coordinates: null,
    },
  },
  driver: {
    driverInfo: {
      fullname: '',
      profileSrc: '',
      rating: 0,
    },
    vehicleInfo: {
      license: '',
      make: '',
      model: '',
    },
    contact: {
      phone: '',
      voipId: '',
      chatRoomId: '',
    },
  },
  trip: {
    expectationId: 0,
    eta: {
      time: 0,
      endTime: 0,
      description: '',
      schedule: null,
    },
    itemType: 0,
    status: '',
    origin: null,
    destinations: [],
    payment: {
      deliveryFee: '',
      cod: '',
      total: '',
      symbol: '',
      payOnDelivery: {
        items: [],
        totalAmount: '',
        totalCashAmount: '',
        totalCashlessAmount: '',
      },
    },
    orderId: '',
  },
  userType: {
    isReceiver: false,
    isSender: false,
  },
  guid: '',
};

export default BookingTracker;
